import React, { useEffect, useMemo, useRef, useState } from 'react';
import qs from 'qs';
import { useLocation } from 'react-router-dom';
import { Alert, Spin, Typography } from 'antd';

import ClientGoogleLogin from '../../../components/client/ClientGoogleLogin';
import ClientEmailLogin from '../../../components/client/ClientEmailLogin';
import { useCurrentUser } from '../../../models/useCurrentUser';
import { payerApiOrigin, PAYER_ORIGIN_MIXED_CONTENT_MESSAGE } from '../../../utils/payerApiOrigin';

import '../../../App.scss';

const { Title, Paragraph } = Typography;

/**
 * Stripe App OAuth (PKCE) entry: user lands here with standard OAuth query params, signs in with
 * Firebase, then we mint a code via strike-payer and redirect back to Stripe Dashboard.
 *
 * First-party, so it auto-approves after sign-in. The generic AI-connection flow with a consent
 * screen is `ClientOAuthAuthorizeScreen` (`/client/oauth/authorize`); both share
 * `utils/payerApiOrigin` for where strike-payer is reached from this page.
 */
function parseOAuthParams(search) {
  const q = qs.parse(search, { ignoreQueryPrefix: true }) || {};
  return {
    response_type: q.response_type,
    client_id: q.client_id,
    redirect_uri: q.redirect_uri,
    state: q.state,
    code_challenge: q.code_challenge,
    code_challenge_method: q.code_challenge_method,
    /** Optional: Strike `clients/{id}` to record under `clients/{id}/apps` (must match signed-in user). */
    strike_client_id: q.strike_client_id,
    stripe_account_id: q.stripe_account_id,
    stripe_user_id: q.stripe_user_id,
  };
}

function paramsValid(p) {
  return (
    p.response_type === 'code'
    && p.client_id
    && p.redirect_uri
    && p.state != null
    && p.code_challenge
    && p.code_challenge_method === 'S256'
  );
}

/** Show full redirect URL on screen (localhost + development only) before sending the browser to Stripe. */
function isLocalHostStripeDemo() {
  return process.env.NODE_ENV === 'development'
    && typeof window !== 'undefined'
    && window.location.hostname === 'localhost';
}

/** DevTools filter: `[StripeAppOAuth]` — does not log Bearer tokens. */
function traceStripeAppOAuth(...args) {
  if (process.env.NODE_ENV === 'development') {
    console.info('[StripeAppOAuth]', ...args);
  }
}

function clearStripeAppOauthPending() {
  try {
    window.sessionStorage.removeItem('strikepay_stripe_app_oauth_pending');
  } catch (e) {
    /* ignore */
  }
}

/** @internal exported for unit tests only */
export const exportedForStripeAppOAuthTests = {
  payerApiOrigin,
  parseOAuthParams,
  paramsValid,
};

export default function ClientStripeAppOAuthScreen() {
  const location = useLocation();
  const [authLoading, user] = useCurrentUser({ signInAnonymously: false });
  const params = useMemo(() => parseOAuthParams(location.search || ''), [location.search]);
  const valid = useMemo(() => paramsValid(params), [params]);

  const [phase, setPhase] = useState('idle'); // idle | exchanging | done | error
  const [errorMessage, setErrorMessage] = useState(null);
  /** Set only on localhost dev: full Stripe redirect URL (incl. code) so you can confirm the target before navigation. */
  const [pendingStripeRedirect, setPendingStripeRedirect] = useState(null);
  const exchangedRef = useRef(false);

  useEffect(() => {
    if (valid && typeof window !== 'undefined') {
      try {
        window.sessionStorage.setItem('strikepay_stripe_app_oauth_pending', '1');
      } catch (e) {
        /* ignore quota / private mode */
      }
    }
  }, [valid]);

  useEffect(() => {
    if (!pendingStripeRedirect || !isLocalHostStripeDemo()) {
      return undefined;
    }
    const id = window.setTimeout(() => {
      window.location.assign(pendingStripeRedirect);
    }, 1200);
    return () => window.clearTimeout(id);
  }, [pendingStripeRedirect]);

  useEffect(() => {
    if (!valid || authLoading || !user || user.isAnonymous) {
      return;
    }
    if (exchangedRef.current || phase === 'done' || phase === 'error') {
      return;
    }

    let cancelled = false;
    (async () => {
      exchangedRef.current = true;
      setPhase('exchanging');
      setErrorMessage(null);
      try {
        const idToken = await user.getIdToken();
        const base = payerApiOrigin();
        if (!base) {
          throw new Error(PAYER_ORIGIN_MIXED_CONTENT_MESSAGE);
        }
        const authCodeUrl = `${base}/v2/api/oauth/stripe-app/authorization-code`;
        traceStripeAppOAuth('POST authorization-code', {
          url: authCodeUrl,
          client_id: params.client_id,
          redirect_uri: params.redirect_uri,
        });
        const oauthPayload = {
          code_challenge: params.code_challenge,
          code_challenge_method: 'S256',
          client_id: params.client_id,
          redirect_uri: params.redirect_uri,
        };
        if (params.strike_client_id) {
          oauthPayload.strike_client_id = String(params.strike_client_id);
        }
        if (params.stripe_account_id) {
          oauthPayload.stripe_account_id = String(params.stripe_account_id);
        }
        if (params.stripe_user_id) {
          oauthPayload.stripe_user_id = String(params.stripe_user_id);
        }
        const res = await fetch(authCodeUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify(oauthPayload),
        });
        const body = await res.json().catch(() => ({}));
        traceStripeAppOAuth('authorization-code response', {
          status: res.status,
          ok: res.ok,
          error: body.error,
          error_description: body.error_description,
        });
        if (!res.ok) {
          const msg = body.error_description || body.error || res.statusText || 'Authorization failed';
          throw new Error(msg);
        }
        const { code } = body;
        if (!code || typeof code !== 'string') {
          throw new Error('Invalid response from strike-payer');
        }
        traceStripeAppOAuth('authorization-code OK; redirecting to Stripe redirect_uri with opaque code');
        // Do not skip redirect when `cancelled` is true: React 18 Strict Mode runs effect cleanup
        // between invocations while the fetch is still in flight, which would strand the UI on
        // "Redirecting to Stripe…" with no navigation. setState below is safe in practice here.
        clearStripeAppOauthPending();
        setPhase('done');
        const target = new URL(params.redirect_uri);
        target.searchParams.set('code', code);
        target.searchParams.set('state', String(params.state));
        const stripeRedirectUrl = target.toString();
        if (isLocalHostStripeDemo()) {
          setPendingStripeRedirect(stripeRedirectUrl);
        } else {
          window.location.assign(stripeRedirectUrl);
        }
      } catch (e) {
        if (cancelled) {
          return;
        }
        traceStripeAppOAuth('authorization-code failed', e instanceof Error ? e.message : e);
        exchangedRef.current = false;
        setPhase('error');
        setErrorMessage(e instanceof Error ? e.message : 'OAuth failed');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [valid, authLoading, user, params, phase]);

  if (!valid) {
    return (
      <div style={{ maxWidth: 560, margin: '48px auto', padding: 24 }}>
        <Title level={3}>Connect Striketip</Title>
        <Alert
          type="error"
          showIcon
          message="Invalid or incomplete OAuth request"
          description="Open this page from the Striketip Stripe app (Log in with Strike). If you are developing, check that query parameters include response_type, client_id, redirect_uri, state, code_challenge, and code_challenge_method."
        />
      </div>
    );
  }

  if (authLoading) {
    return (
      <div style={{ textAlign: 'center', marginTop: 64 }}>
        <Spin size="large" />
        <Paragraph style={{ marginTop: 16 }}>Loading…</Paragraph>
      </div>
    );
  }

  if (!user || user.isAnonymous) {
    return (
      <div style={{ maxWidth: 480, margin: '32px auto', padding: 16 }}>
        <Title level={3}>Sign in to Strike</Title>
        <Paragraph type="secondary">
          Sign in with the same account you use in the Strike client dashboard. After sign-in you will
          be sent back to Stripe to finish connecting the app.
        </Paragraph>
        {/* The custom login widgets are dark-themed (built for the black login screen),
            so host them in a dark card here. Bare sign-in — the auth-state change re-renders
            this screen past the gate and continues the Stripe OAuth handoff. */}
        <div style={{ background: '#000', borderRadius: 8, padding: '20px 0', marginTop: 8 }}>
          <div style={{ padding: '0 24px', marginBottom: 8 }}>
            <ClientGoogleLogin />
          </div>
          <ClientEmailLogin />
        </div>
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div style={{ maxWidth: 560, margin: '48px auto', padding: 24 }}>
        <Alert
          type="error"
          showIcon
          message="Could not connect to Stripe"
          description={errorMessage}
        />
        <Paragraph style={{ marginTop: 16 }}>
          Check strike-payer is running; <code>STRIPE_APP_OAUTH_TEST_URI</code> /{' '}
          <code>STRIPE_APP_OAUTH_LIVE_URI</code> and optional <code>STRIPE_APP_TEST_ID</code> /{' '}
          <code>STRIPE_APP_LIVE_ID</code> must match this app&apos;s Stripe OAuth client id and redirect
          URLs. Payer base:{' '}
          <code>{payerApiOrigin() ?? 'set REACT_APP_STRIKE_PAYER_API_ORIGIN (required when this page is HTTPS)'}</code>
        </Paragraph>
      </div>
    );
  }

  if (pendingStripeRedirect) {
    return (
      <div style={{ textAlign: 'center', marginTop: 64, padding: '0 16px' }}>
        <Spin size="large" />
        <Paragraph style={{ marginTop: 16 }}>Redirecting to Stripe…</Paragraph>
        <Paragraph type="secondary" style={{ marginTop: 12, fontSize: 12, wordBreak: 'break-all' }}>
          Localhost demo — target URL:
        </Paragraph>
        <Paragraph copyable={{ text: pendingStripeRedirect }} style={{ marginTop: 8, wordBreak: 'break-all', fontFamily: 'monospace', fontSize: 11 }}>
          {pendingStripeRedirect}
        </Paragraph>
      </div>
    );
  }

  return (
    <div style={{ textAlign: 'center', marginTop: 64 }}>
      <Spin size="large" />
      <Paragraph style={{ marginTop: 16 }}>Redirecting to Stripe…</Paragraph>
    </div>
  );
}
